import { useState, useEffect, useCallback, useRef } from 'react';
import { Lead, Project, Category } from '../types';
import { leadsService, projectsService, categoriesService } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

type UseSupabaseDataOptions = {
  include?: Partial<{
    leads: boolean;
    projects: boolean;
    categories: boolean;
  }>;
};

export const useSupabaseData = (options: UseSupabaseDataOptions = {}) => {
  const include = {
    leads: options.include?.leads ?? true,
    projects: options.include?.projects ?? true,
    categories: options.include?.categories ?? true,
  };

  const { user, loading: authLoading } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const leadNextActionSeqRef = useRef<Record<string, number>>({});
  const leadRangeLoadSeqRef = useRef(0);

  // Load all data only when user is authenticated
  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    
    // Strict check: user must be defined and have an id
    if (!user || !user.id) {
      // Not authenticated: don't keep an infinite spinner
      setLoading(false);
      setError(null);
      setLeads([]);
      setProjects([]);
      setCategories([]);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load selected resources in parallel (much faster than sequential)
        const tasks: Array<Promise<void>> = [];

        if (include.leads) {
          tasks.push(
            leadsService.getAll().then((d) => setLeads(d))
          );
        } else {
          setLeads([]);
        }

        if (include.projects) {
          tasks.push(
            projectsService.getAll().then((d) => setProjects(d))
          );
        } else {
          setProjects([]);
        }

        if (include.categories) {
          tasks.push(
            categoriesService.getAll().then((d) => setCategories(d))
          );
        } else {
          setCategories([]);
        }

        await Promise.all(tasks);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data from database');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading, include.leads, include.projects, include.categories]); // scoped by what we load

  // =====================================================
  // LEADS - Local optimistic patch (for pages that mutate leads)
  // =====================================================
  const updateLeadOptimistic = useCallback((leadId: string, patch: Partial<Lead>) => {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, ...patch, updatedAt: new Date() } : l));
  }, []);

  /**
   * Optimistic update of a lead's next action date.
   * - Pass a Date to set the date, or null to clear it in the DB (undefined is not sent to the API).
   * - Persists via patch, then applies server response; rolls back only if still the latest mutation for that lead.
   */
  const updateLeadNextActionAt = useCallback(async (leadId: string, nextActionAt?: Date | null) => {
    const lead = leads.find(l => l.id === leadId);
    const previousNext = lead?.nextActionAt;
    const previousLast = lead?.lastActionAt;
    const seq = (leadNextActionSeqRef.current[leadId] ?? 0) + 1;
    leadNextActionSeqRef.current[leadId] = seq;

    const now = new Date();
    updateLeadOptimistic(leadId, { nextActionAt, lastActionAt: now });

    const updatedLead = await leadsService.update(leadId, { nextActionAt, lastActionAt: now });

    // If a newer mutation happened, don't touch state (no rollback either).
    if ((leadNextActionSeqRef.current[leadId] ?? 0) !== seq) {
      return { ok: Boolean(updatedLead), lead: updatedLead };
    }

    if (updatedLead) {
      updateLeadOptimistic(leadId, updatedLead);
      return { ok: true, lead: updatedLead };
    }

    // Rollback
    updateLeadOptimistic(leadId, { nextActionAt: previousNext, lastActionAt: previousLast });
    return { ok: false, lead: null };
  }, [leads, updateLeadOptimistic]);

  // Load follow-up leads only for a given visible calendar range (performance)
  const loadLeadFollowupsInRange = useCallback(async (start: Date, end: Date) => {
    const seq = ++leadRangeLoadSeqRef.current;
    try {
      const next = await leadsService.getFollowupsInRange(start, end);
      if (seq !== leadRangeLoadSeqRef.current) return;
      setLeads(next);
    } catch (err) {
      console.error('Error loading followups in range:', err);
      // Keep previous list; don't blow up page-level error for a calendar optimization.
    }
  }, []);

  return {
    // Data (read-only for most pages, use dedicated hooks for CRUD)
    leads,
    projects,
    categories,

    // Loading states
    loading,
    error,

    // Leads - helpers (CRUD via useInfiniteLeads)
    updateLeadOptimistic,
    updateLeadNextActionAt,
    loadLeadFollowupsInRange,
  };
};


